// src/views/ReportsHome.js

import React, { useState, useEffect, useMemo } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { 
    BarChart2, 
    AlertCircle, 
    AlertTriangle, 
    XCircle, 
    TrendingUp, 
    DollarSign, 
    CreditCard, 
    Wallet, 
    Clock, 
    Users, 
    PieChart, 
    Calendar, 
    Scale,
    Package,
    Award
} from 'lucide-react';
import { DOC_TYPES, resolveDocTerminology } from '../utils/quoteSchema'; // qreport-5b8e2c64
import { db } from '../firebase/config';
import { collection, onSnapshot } from 'firebase/firestore'; 
import { 
    startOfYear, 
    endOfYear, 
    startOfMonth, 
    endOfMonth, 
    startOfQuarter, 
    endOfQuarter, 
    format, 
    parseISO 
} from 'date-fns';
import { formatCurrency } from '../utils/formatters';

// --- 1. INTERACTIVE KPI CARD COMPONENT ---
const KpiCard = ({ title, amount, icon, colorClass, subtext, onClick }) => (
    <div 
        onClick={onClick}
        className={`
            bg-white p-6 rounded-lg border shadow-sm flex items-center justify-between transition-all duration-200 
            ${onClick ? 'cursor-pointer hover:shadow-md hover:border-blue-300 hover:bg-gray-50' : ''}
        `}
    >
        <div>
            <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
            <h3 className={`text-2xl font-bold ${colorClass}`}>
                {amount}
            </h3>
            {subtext && (
                <p className="text-xs text-gray-400 mt-1">{subtext}</p>
            )}
        </div>
        <div className={`p-3 rounded-full ${colorClass.replace('text-', 'bg-').replace('600', '100')}`}>
            {icon}
        </div>
    </div>
);

// --- 2. REPORT NAVIGATION LINK ---
const ReportLinkCard = ({ to, title, description, icon }) => (
    <Link 
        to={to} 
        className="block p-6 bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow group"
    >
        <div className="flex items-center mb-2">
            <div className="group-hover:scale-110 transition-transform duration-200">
                {icon}
            </div>
            <h2 className="text-lg font-bold text-gray-800 ml-3 group-hover:text-blue-600 transition-colors">
                {title}
            </h2>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
            {description}
        </p>
    </Link>
);

// qreport-5b8e2c64 — the Reports page a Sales Consultant sees: their own
// Estimates report and nothing else. Deliberately a separate component, so it
// opens NONE of ReportsHome's invoice/expense listeners and shows no KPI.
// CrmApp renders it for a scoped viewer at exactly /reports; every other
// report stays behind VIEW_REPORTS.
export const EstimatesOnlyReportsHome = ({ companySettings }) => {
    const { wordPlural } = resolveDocTerminology(companySettings?.quoteTerminologyDefault || DOC_TYPES.QUOTE);
    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Reports</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <ReportLinkCard
                    to="/reports/estimates"
                    title={`My ${wordPlural} Report`}
                    description={`Your win rate, open value, and why your ${wordPlural.toLowerCase()} were won or lost.`}
                    icon={<Award size={24} className="text-blue-600" />}
                />
            </div>
        </div>
    );
};

const ReportsHome = ({ currentUserProfile, companySettings }) => {
    // qreport-5b8e2c64 — aggregate surface: the ORG default terminology.
    const { wordPlural: docWordPlural } = resolveDocTerminology(companySettings?.quoteTerminologyDefault || DOC_TYPES.QUOTE);
    // --- STATE MANAGEMENT ---
    const [invoices, setInvoices] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Date Filtering State
    const [timeRange, setTimeRange] = useState('YTD'); 
    const [customStart, setCustomStart] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [customEnd, setCustomEnd] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    
    const navigate = useNavigate();

    // --- DATA FETCHING ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) {
            setLoading(false);
            return;
        }
        
        const orgId = currentUserProfile.organizationId;

        // 1. Listen to Invoices
        const unsubInvoices = onSnapshot(collection(db, `organizations/${orgId}/invoices`), (snap) => {
            const invoiceData = snap.docs.map(d => ({ 
                id: d.id, 
                ...d.data() 
            }));
            setInvoices(invoiceData);
        });

        // 2. Listen to Expenses
        const unsubExpenses = onSnapshot(collection(db, `organizations/${orgId}/expenses`), (snap) => {
            const expenseData = snap.docs.map(d => ({ 
                id: d.id, 
                ...d.data() 
            }));
            setExpenses(expenseData);
        });

        setLoading(false);

        return () => {
            unsubInvoices();
            unsubExpenses();
        };
    }, [currentUserProfile]);

    // --- METRICS ENGINE ---
    const metrics = useMemo(() => {
        const now = new Date();
        let start, end;

        // 1. Determine Date Range
        switch (timeRange) {
            case 'MTD':
                start = startOfMonth(now);
                end = endOfMonth(now);
                break;
            case 'QTD':
                start = startOfQuarter(now);
                end = endOfQuarter(now);
                break;
            case 'YTD':
                start = startOfYear(now);
                end = endOfYear(now);
                break;
            case 'Custom':
                start = parseISO(customStart);
                end = parseISO(customEnd);
                end.setHours(23, 59, 59, 999); 
                break;
            default:
                start = startOfYear(now);
                end = endOfYear(now);
        }

        const getInvoiceDate = (inv) => {
            if (inv.createdAt?.toDate) return inv.createdAt.toDate();
            if (inv.issuedAt?.toDate) return inv.issuedAt.toDate();
            if (inv.date) return new Date(inv.date);
            return new Date(); 
        };

        // 3. Filter Invoices (Accrual Basis)
        const filteredInvoices = invoices.filter(inv => {
            if (['Draft', 'Void', 'Cancelled'].includes(inv.status)) return false;
            const date = getInvoiceDate(inv);
            return date >= start && date <= end;
        });

        // 4. Filter Expenses
        const filteredExpenses = expenses.filter(exp => {
            const date = exp.date?.toDate ? exp.date.toDate() : new Date(exp.date);
            return date >= start && date <= end;
        });

        // A. REVENUE (Total Invoiced)
        const totalRevenue = filteredInvoices
            .reduce((sum, inv) => sum + (parseFloat(inv.total) || 0), 0);

        // B. OUTSTANDING (Accounts Receivable)
        const totalOutstanding = filteredInvoices
            .filter(inv => inv.status !== 'Paid' && inv.status !== 'Written Off') // s0bwoff-f74fb923: bad debt is not receivable
            .reduce((sum, inv) => {
                const balance = inv.balanceDue !== undefined ? inv.balanceDue : inv.total;
                return sum + (parseFloat(balance) || 0);
            }, 0);

        // C. EXPENSES
        const totalExpenses = filteredExpenses
            .reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

        // D. NET PROFIT (Accrual)
        const netProfit = totalRevenue - totalExpenses;

        return { 
            totalRevenue, 
            totalOutstanding, 
            totalExpenses, 
            netProfit 
        };
    }, [invoices, expenses, timeRange, customStart, customEnd]);

    // --- REMOVED THE BLOCKING PERMISSION CHECK HERE ---
    // The previous code explicitly blocked anyone who wasn't exactly 'Admin' or 'Manager'.
    // We removed it so Owners can pass through.

    if (loading) {
        return <div className="p-10 text-center text-gray-500">Loading Financial Data...</div>;
    }

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
            
            {/* HEADER SECTION */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-end mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Financial Overview</h1>
                    <p className="text-gray-500 mt-1">
                        Performance Report ({timeRange === 'Custom' ? `${customStart} to ${customEnd}` : timeRange})
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 items-end sm:items-center">
                    
                    {/* CUSTOM DATE PICKERS */}
                    {timeRange === 'Custom' && (
                        <div className="flex items-center gap-2 bg-white p-1.5 border rounded-md shadow-sm animate-in fade-in slide-in-from-right-4 duration-200">
                            <input 
                                type="date" 
                                value={customStart} 
                                onChange={(e) => setCustomStart(e.target.value)}
                                className="text-sm border-none focus:ring-0 text-gray-700 p-0"
                            />
                            <span className="text-gray-400">-</span>
                            <input 
                                type="date" 
                                value={customEnd} 
                                onChange={(e) => setCustomEnd(e.target.value)}
                                className="text-sm border-none focus:ring-0 text-gray-700 p-0"
                            />
                        </div>
                    )}

                    {/* RANGE TOGGLES */}
                    <div className="bg-white border rounded-md shadow-sm p-1 flex">
                        {['MTD', 'QTD', 'YTD', 'Custom'].map((range) => (
                            <button
                                key={range}
                                onClick={() => setTimeRange(range)}
                                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    timeRange === range
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                {range}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* KPI CARDS SECTION */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                
                {/* 1. TOTAL REVENUE */}
                <KpiCard 
                    title={`Total Revenue (${timeRange})`} 
                    amount={formatCurrency(metrics.totalRevenue)}
                    icon={<DollarSign size={24} className="text-green-600" />}
                    colorClass="text-green-600"
                    subtext="Total invoiced sales (Accrual)"
                    onClick={() => navigate('/reports/sales')} 
                />

                {/* 2. NET PROFIT */}
                <KpiCard 
                    title={`Net Profit (${timeRange})`} 
                    amount={formatCurrency(metrics.netProfit)}
                    icon={<TrendingUp size={24} className={metrics.netProfit >= 0 ? "text-blue-600" : "text-red-600"} />}
                    colorClass={metrics.netProfit >= 0 ? "text-blue-600" : "text-red-600"}
                    subtext="Revenue - Expenses"
                    onClick={() => navigate('/reports/profit-loss')} 
                />

                {/* 3. OUTSTANDING AR */}
                <KpiCard 
                    title="Outstanding AR" 
                    amount={formatCurrency(metrics.totalOutstanding)}
                    icon={<AlertCircle size={24} className="text-orange-600" />}
                    colorClass="text-orange-600"
                    subtext={`Unpaid invoices from period`}
                    onClick={() => navigate('/reports/collections')} 
                />

                {/* 4. EXPENSES */}
                <KpiCard 
                    title={`Expenses (${timeRange})`} 
                    amount={formatCurrency(metrics.totalExpenses)}
                    icon={<CreditCard size={24} className="text-red-600" />}
                    colorClass="text-red-600"
                    subtext="Cost of doing business"
                    onClick={() => navigate('/expenses')}
                />
            </div>

            <div className="border-t border-gray-200 my-8"></div>

            {/* DETAILED REPORTS GRID */}
            <h2 className="text-xl font-bold text-gray-800 mb-6">Detailed Reports</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                
                {/* qreport-5b8e2c64 (Quote Seam 12a) — estimate win rates live here now. */}
                <ReportLinkCard
                    to="/reports/estimates"
                    title={`${docWordPlural} Report`}
                    description={`Win rate, open value by stage, and why ${docWordPlural.toLowerCase()} were won or lost.`}
                    icon={<Award size={24} className="text-blue-600" />}
                />

                <ReportLinkCard
                    to="/reports/sales"
                    title="Sales Performance"
                    description="Invoiced sales, cash collected, and top customers."
                    icon={<TrendingUp size={24} className="text-green-600" />}
                />

                <ReportLinkCard
                    to="/reports/profit-loss"
                    title="Profit & Loss"
                    description="Income vs Expenses breakdown by month."
                    icon={<Scale size={24} className="text-blue-600" />}
                />

                <ReportLinkCard
                    to="/reports/expenses" 
                    title="Expense Analysis"
                    description="Review categorized spending, vendor breakdowns, and user activity."
                    icon={<PieChart size={24} className="text-indigo-600" />}
                />

                <ReportLinkCard
                    to="/reports/collections"
                    title="Collections Report"
                    description="View overdue accounts and send bulk payment reminders."
                    icon={<AlertTriangle size={24} className="text-orange-600" />}
                />

                {/* stalecred-9d0b7f38 (Seam 13) -- money HELD FOR customers, opposite of Collections. */}
                <ReportLinkCard
                    to="/reports/stale-credits"
                    title="Stale Account Credits"
                    description="Money you're holding for customers, unapplied for 12+ months, with a manual write-off."
                    icon={<CreditCard size={24} className="text-purple-600" />}
                />

                <ReportLinkCard
                    to="/reports/profitability"
                    title="Job Profitability"
                    description="Deep dive into margins and costs on completed jobs."
                    icon={<Wallet size={24} className="text-teal-600" />}
                />

                <ReportLinkCard
                    to="/reports/team"
                    title="Team Performance"
                    description="Revenue generation and efficiency per employee."
                    icon={<Users size={24} className="text-purple-600" />}
                />

                <ReportLinkCard
                    to="/reports/overdue-jobs"
                    title="Overdue Jobs"
                    description="Revenue work (Installations/Service) past due."
                    icon={<AlertCircle size={24} className="text-red-600" />}
                />

                <ReportLinkCard
                    to="/reports/overdue"
                    title="Overdue Events"
                    description="Missed calls, appointments, and follow-ups."
                    icon={<Clock size={24} className="text-orange-500" />}
                />

                <ReportLinkCard
                    to="/reports/cancellations"
                    title="Cancellation Log"
                    description="Review lost jobs and reasons."
                    icon={<XCircle size={24} className="text-gray-600" />}
                />

                <ReportLinkCard
                    to="/reports/service-revenue"
                    title="Service & Template Revenue"
                    description="Which services and job templates are driving the most revenue, volume, and margin."
                    icon={<Package size={24} className="text-violet-600" />}
                />
            </div>
        </div>
    );
};

export default ReportsHome;